'use client';

import { useState, useRef, useEffect, type ComponentType } from 'react';
import { RefreshCw, X } from 'lucide-react';
import { useGesture } from '@use-gesture/react';
import { cn } from '../utils/cn';

interface ImagePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  src: string;
  alt?: string;
  /** Optional secondary action shown to the left of the Close button.
   * Used by FeedDetailScreen / HistoryDetailScreen to surface
   * "Повторить" inside the fullscreen view, so the user doesn't have
   * to dismiss the modal just to find the repeat CTA underneath. */
  primaryAction?: {
    label: string;
    icon?: ComponentType<{ className?: string }>;
    /** Defaults to closing the modal first, then invoking onClick on
     * the next tick — keeps the parent's nav transition (e.g. from
     * FeedDetailScreen to the home generator) clean. Set to `false`
     * to keep the modal open while the action runs. */
    closeOnClick?: boolean;
    onClick: () => void;
  };
}

export function ImagePreviewModal({
  isOpen,
  onClose,
  src,
  alt = 'Preview',
  primaryAction,
}: ImagePreviewModalProps) {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      setScale(1);
      setPosition({ x: 0, y: 0 });
    }
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  useGesture(
    {
      onPinch: ({ offset: [s] }) => {
        setScale(Math.min(Math.max(s, 1), 4));
      },
      onDrag: ({ offset: [x, y], pinching }) => {
        if (pinching || scale <= 1) return;
        setPosition({ x, y });
      },
      onPinchEnd: () => {
        if (scale <= 1) {
          setPosition({ x: 0, y: 0 });
        }
      },
    },
    {
      target: imageRef,
      pinch: { scaleBounds: { min: 1, max: 4 } },
      drag: {
        from: () => [position.x, position.y],
        bounds: () => {
          if (scale <= 1) return { left: 0, right: 0, top: 0, bottom: 0 };
          const bound = 100 * (scale - 1);
          return { left: -bound, right: bound, top: -bound, bottom: bound };
        },
      },
    }
  );

  const handleDoubleClick = () => {
    if (scale > 1) {
      setScale(1);
      setPosition({ x: 0, y: 0 });
    } else {
      setScale(2);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 z-50 flex items-center justify-center"
    >
      <div
        className="absolute inset-0 animate-in fade-in bg-black/70 duration-200 backdrop-blur-sm"
        onClick={handleBackdropClick}
      />

      <div
        className="relative z-10 flex h-full w-full animate-in zoom-in-95 items-center justify-center p-4 fade-in duration-300"
        onClick={handleBackdropClick}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          ref={imageRef}
          src={src}
          alt={alt}
          className="max-h-[80vh] max-w-full touch-none select-none rounded-lg object-contain"
          style={{
            transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
            transition: scale === 1 ? 'transform 0.2s ease-out' : undefined,
          }}
          draggable={false}
          onDoubleClick={handleDoubleClick}
          onClick={(e) => e.stopPropagation()}
        />
      </div>

      <div className="absolute bottom-6 right-6 z-20 flex items-center gap-2">
        {primaryAction && (
          <button
            onClick={() => {
              const closeFirst = primaryAction.closeOnClick !== false;
              if (closeFirst) {
                onClose();
                // Defer the action so the modal's exit transition gets
                // a tick to start before any parent navigation kicks in.
                window.setTimeout(() => primaryAction.onClick(), 0);
              } else {
                primaryAction.onClick();
              }
            }}
            className={cn(
              'flex items-center gap-2 rounded-full',
              'bg-primary/95 backdrop-blur-md',
              'px-4 py-2.5 text-sm font-semibold text-primary-foreground',
              'animate-in slide-in-from-bottom-2 fade-in duration-300',
              'transition-all hover:bg-primary active:scale-95',
              'shadow-[0_4px_12px_rgba(0,0,0,0.25)]'
            )}
          >
            {primaryAction.icon ? (
              <primaryAction.icon className="h-4 w-4" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            <span>{primaryAction.label}</span>
          </button>
        )}
        <button
          onClick={onClose}
          className={cn(
            'flex items-center gap-2 rounded-full',
            'border border-white/20 bg-white/10 backdrop-blur-md',
            'px-4 py-2.5 text-sm font-medium text-white',
            'animate-in slide-in-from-bottom-2 fade-in duration-300',
            'transition-all hover:bg-white/20 active:scale-95'
          )}
        >
          <span>Закрыть</span>
          <X className="h-4 w-4" />
        </button>
      </div>

      {scale === 1 && (
        <div className="absolute bottom-6 left-6 z-20 animate-in slide-in-from-bottom-2 fade-in duration-300">
          <p className="text-xs text-white/60">
            Разведите пальцы для увеличения
          </p>
        </div>
      )}
    </div>
  );
}
